import { useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { SwitchCamera } from "lucide-react";
import { toast } from "sonner";

let __qrCounter = 0;

// Robust QR scanner: single camera stream (no double), front/back switch.
// Lifecycle ops are serialized on a promise chain so cleanup always stops the
// stream even if it fires before start() resolves (React double-mount / StrictMode).
export function QrScanner({ onScan, transform, testId = "qr-reader", qrbox = 220 }) {
  const [elId] = useState(() => `qr-reader-${++__qrCounter}`);
  const qrRef = useRef(null);
  const opRef = useRef(Promise.resolve());
  const camsRef = useRef([]);
  const idxRef = useRef(0);
  const lastRef = useRef("");
  const [canSwitch, setCanSwitch] = useState(false);
  const onScanRef = useRef(onScan);
  const transformRef = useRef(transform);
  onScanRef.current = onScan;
  transformRef.current = transform;

  const handle = useCallback((text) => {
    if (text === lastRef.current) return;
    lastRef.current = text;
    const t = transformRef.current;
    onScanRef.current(t ? t(text) : text);
    setTimeout(() => (lastRef.current = ""), 2500);
  }, []);

  const startCam = useCallback((qr, camId) => qr.start(camId, { fps: 10, qrbox }, handle, () => {}), [handle, qrbox]);

  useEffect(() => {
    let cancelled = false;
    const qr = new Html5Qrcode(elId, { verbose: false });
    qrRef.current = qr;

    const run = (async () => {
      try {
        const cams = await Html5Qrcode.getCameras();
        if (cancelled || !cams?.length) {
          if (!cancelled) await startCam(qr, { facingMode: "environment" });
          return;
        }
        camsRef.current = cams;
        setCanSwitch(cams.length > 1);
        const backIdx = cams.findIndex((c) => /back|rear|environment/i.test(c.label || ""));
        idxRef.current = backIdx >= 0 ? backIdx : 0;
        if (cancelled) return;
        await startCam(qr, cams[idxRef.current].id);
      } catch {
        if (!cancelled) toast.error("Kamera tidak dapat diakses");
      }
    })();
    opRef.current = run;

    return () => {
      cancelled = true;
      opRef.current = run.then(async () => {
        try { if (qr.isScanning) await qr.stop(); } catch { /* noop */ }
        try { qr.clear(); } catch { /* noop */ }
      });
    };
  }, [elId, startCam]);

  const switchCam = useCallback(() => {
    const qr = qrRef.current;
    const cams = camsRef.current;
    if (!qr || cams.length < 2) return;
    opRef.current = opRef.current.then(async () => {
      try { if (qr.isScanning) await qr.stop(); } catch { /* noop */ }
      idxRef.current = (idxRef.current + 1) % cams.length;
      try { await startCam(qr, cams[idxRef.current].id); }
      catch { toast.error("Gagal mengganti kamera"); }
    });
  }, [startCam]);

  return (
    <div className="relative">
      <div id={elId} className="rounded-2xl overflow-hidden bg-slate-900" data-testid={testId} />
      {canSwitch && (
        <button
          type="button"
          onClick={switchCam}
          data-testid="qr-switch-camera"
          className="absolute top-2 right-2 z-10 inline-flex items-center gap-1.5 bg-black/60 text-white text-xs font-bold rounded-full px-3 py-2 backdrop-blur-sm active:scale-95 transition"
        >
          <SwitchCamera className="w-4 h-4" /> Ganti Kamera
        </button>
      )}
    </div>
  );
}
